// Resolves request stage naming across primary and legacy request paths.
const resolveRequestStageName = (usedLegacyRequest = false) => {
  return usedLegacyRequest ? 'request-legacy' : 'request';
};

// Builds logging and audit artifacts for a successful request attempt.
export const createRequestAttemptSuccessArtifacts = ({
  attemptIndex,
  attemptKey = null,
  usedLegacyRequest = false,
  candidateModel = null,
  responseModel = null,
  stream = false,
  durationMs = 0,
  usage = null,
  cost = null,
  requestId = null,
  providerRequestId = null,
  callKind = 'response',
  requestExcerpt = null,
  responseExcerpt = null
} = {}) => {
  const stageName = resolveRequestStageName(usedLegacyRequest);
  const transport = stream ? 'stream' : 'single';
  const normalizedUsage = usage && typeof usage === 'object'
    ? usage
    : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
  const normalizedCost = cost && typeof cost === 'object' ? cost : {};
  const normalizedPromptCostUsd = Number.isFinite(Number(normalizedCost.promptCostUsd)) ? Number(normalizedCost.promptCostUsd) : 0;
  const normalizedCompletionCostUsd = Number.isFinite(Number(normalizedCost.completionCostUsd)) ? Number(normalizedCost.completionCostUsd) : 0;
  const normalizedCostUsd = Number.isFinite(Number(normalizedCost.totalCostUsd))
    ? Number(normalizedCost.totalCostUsd)
    : Number((normalizedPromptCostUsd + normalizedCompletionCostUsd).toFixed(6));

  return {
    logInput: {
      attemptIndex,
      attemptHash: attemptKey,
      stageName,
      callKind,
      providerName: 'openrouter',
      providerRequestId,
      candidateModel,
      responseModel,
      transport,
      status: 'success',
      durationMs,
      usage: normalizedUsage,
      cost: {
        promptCostUsd: normalizedPromptCostUsd,
        completionCostUsd: normalizedCompletionCostUsd,
        totalCostUsd: normalizedCostUsd
      },
      requestExcerpt,
      responseExcerpt,
      metadata: {
        requestId,
        streamed: Boolean(stream),
        legacyRequest: Boolean(usedLegacyRequest)
      }
    },
    auditEntry: {
      attemptIndex,
      attemptKey: attemptKey || null,
      stageName,
      callKind,
      providerName: 'openrouter',
      providerRequestId: providerRequestId || null,
      candidateModel: candidateModel || null,
      responseModel: responseModel || null,
      transport,
      status: 'success',
      isTimeout: false,
      durationMs: Number(durationMs || 0) || 0,
      usage: normalizedUsage,
      cost: {
        promptCostUsd: normalizedPromptCostUsd,
        completionCostUsd: normalizedCompletionCostUsd,
        totalCostUsd: normalizedCostUsd
      },
      costUsd: normalizedCostUsd,
      errorCode: null
    }
  };
};

// Builds logging and audit artifacts for a failed request attempt.
export const createRequestAttemptFailureArtifacts = ({
  attemptIndex,
  attemptKey = null,
  usedLegacyRequest = false,
  candidateModel = null,
  responseModel = null,
  stream = false,
  durationMs = 0,
  error = null,
  requestId = null,
  providerRequestId = null,
  callKind = 'response',
  requestExcerpt = null
} = {}) => {
  const stageName = resolveRequestStageName(usedLegacyRequest);
  const transport = stream ? 'stream' : 'single';
  const rawErrorCode = String(error?.code || '').trim();
  const errorMessage = String(error?.message || '').trim() || 'request attempt failed';
  const statusCode = Number(error?.status || 0) || null;
  const timeoutLike = rawErrorCode === 'AI_TIMEOUT'
    || rawErrorCode === 'AI_REQUEST_ABORTED'
    || String(error?.name || '').toLowerCase() === 'aborterror'
    || /timed out|timeout|cancelled before completion/i.test(errorMessage);
  const errorCode = rawErrorCode || (timeoutLike ? 'REQUEST_TIMEOUT' : null);
  const normalizedStatus = timeoutLike ? 'timeout' : 'failed';

  return {
    logInput: {
      attemptIndex,
      attemptHash: attemptKey,
      stageName,
      callKind,
      providerName: 'openrouter',
      providerRequestId,
      candidateModel,
      responseModel,
      transport,
      status: normalizedStatus,
      isTimeout: timeoutLike,
      durationMs,
      errorCode,
      errorMessage,
      requestExcerpt,
      metadata: {
        requestId,
        status: statusCode,
        legacyRequest: Boolean(usedLegacyRequest)
      }
    },
    auditEntry: {
      attemptIndex,
      attemptKey: attemptKey || null,
      stageName,
      callKind,
      providerName: 'openrouter',
      providerRequestId: providerRequestId || null,
      candidateModel: candidateModel || null,
      responseModel: responseModel || null,
      transport,
      status: normalizedStatus,
      isTimeout: timeoutLike,
      durationMs: Number(durationMs || 0) || 0,
      usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
      cost: { promptCostUsd: 0, completionCostUsd: 0, totalCostUsd: 0 },
      costUsd: 0,
      errorCode
    }
  };
};

// Aggregates per-attempt usage, cost, duration, and status rollups.
export const createRequestRollupArtifacts = ({
  attempts = []
} = {}) => {
  const normalizedAttempts = Array.isArray(attempts) ? attempts : [];

  const rollupUsage = normalizedAttempts.reduce((acc, attempt) => {
    const usageValue = attempt?.usage && typeof attempt.usage === 'object' ? attempt.usage : {};
    acc.promptTokens += Number(usageValue.promptTokens || 0) || 0;
    acc.completionTokens += Number(usageValue.completionTokens || 0) || 0;
    acc.totalTokens += Number(usageValue.totalTokens || 0) || 0;
    return acc;
  }, { promptTokens: 0, completionTokens: 0, totalTokens: 0 });

  const rollupCostUsd = normalizedAttempts.reduce((sum, attempt) => {
    const value = Number(attempt?.costUsd || 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

  const rollupDurationMs = normalizedAttempts.reduce((sum, attempt) => {
    const value = Number(attempt?.durationMs || 0);
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0);

  const rollupFailedAttempts = normalizedAttempts
    .filter(attempt => String(attempt?.status || '').toLowerCase() === 'failed')
    .length;

  const rollupTimeoutAttempts = normalizedAttempts
    .filter(attempt => String(attempt?.status || '').toLowerCase() === 'timeout' || Boolean(attempt?.isTimeout))
    .length;

  const attemptsIndex = normalizedAttempts.map((attempt) => ({
    attemptIndex: Number(attempt?.attemptIndex || 0),
    attemptKey: attempt?.attemptKey || null,
    stageName: attempt?.stageName || 'request',
    callKind: attempt?.callKind || 'response',
    providerName: attempt?.providerName || 'openrouter',
    providerRequestId: attempt?.providerRequestId || null,
    candidateModel: attempt?.candidateModel || null,
    responseModel: attempt?.responseModel || null,
    transport: attempt?.transport || null,
    status: attempt?.status || 'failed',
    isTimeout: Boolean(attempt?.isTimeout),
    durationMs: Number(attempt?.durationMs || 0) || 0,
    promptTokens: Number(attempt?.usage?.promptTokens || 0) || 0,
    completionTokens: Number(attempt?.usage?.completionTokens || 0) || 0,
    totalTokens: Number(attempt?.usage?.totalTokens || 0) || 0,
    promptCostUsd: Number(attempt?.cost?.promptCostUsd || 0) || 0,
    completionCostUsd: Number(attempt?.cost?.completionCostUsd || 0) || 0,
    costUsd: Number(attempt?.costUsd || 0) || 0,
    errorCode: attempt?.errorCode || null
  }));

  const attemptsSummary = {
    stats: {
      totalCalls: normalizedAttempts.length,
      successCalls: normalizedAttempts.filter(attempt => String(attempt?.status || '').toLowerCase() === 'success').length,
      failedCalls: rollupFailedAttempts,
      timeoutCalls: rollupTimeoutAttempts,
      attemptCount: normalizedAttempts.length,
      failedAttemptCount: rollupFailedAttempts,
      timeoutAttemptCount: rollupTimeoutAttempts,
      totalDurationMs: rollupDurationMs,
      totalPromptTokens: Number(rollupUsage.promptTokens || 0) || 0,
      totalCompletionTokens: Number(rollupUsage.completionTokens || 0) || 0,
      totalTokens: Number(rollupUsage.totalTokens || 0) || 0,
      totalPromptCostUsd: Number(normalizedAttempts.reduce((sum, attempt) => sum + (Number(attempt?.cost?.promptCostUsd || 0) || 0), 0).toFixed(6)),
      totalCompletionCostUsd: Number(normalizedAttempts.reduce((sum, attempt) => sum + (Number(attempt?.cost?.completionCostUsd || 0) || 0), 0).toFixed(6)),
      totalCostUsd: Number(rollupCostUsd.toFixed(6))
    },
    attempts: attemptsIndex,
    modelBreakdown: normalizedAttempts.reduce((acc, attempt) => {
      const key = String(attempt?.responseModel || attempt?.candidateModel || 'unknown').trim() || 'unknown';
      const current = acc[key] || {
        callCount: 0,
        successCount: 0,
        failedCount: 0,
        timeoutCount: 0,
        totalTokens: 0,
        totalCostUsd: 0
      };
      const status = String(attempt?.status || '').toLowerCase();
      current.callCount += 1;
      current.successCount += status === 'success' ? 1 : 0;
      current.failedCount += status === 'failed' ? 1 : 0;
      current.timeoutCount += (status === 'timeout' || Boolean(attempt?.isTimeout)) ? 1 : 0;
      current.totalTokens += Number(attempt?.usage?.totalTokens || 0) || 0;
      current.totalCostUsd = Number((current.totalCostUsd + (Number(attempt?.costUsd || 0) || 0)).toFixed(6));
      acc[key] = current;
      return acc;
    }, {})
  };

  return {
    rollupUsage,
    rollupCostUsd,
    rollupDurationMs,
    rollupFailedAttempts,
    rollupTimeoutAttempts,
    attemptsSummary,
    attemptsIndex
  };
};

// Normalizes request-log audit shape for pending/success/failure states.
export const createRequestLogAuditRecord = ({
  enabled = false,
  status = 'skipped',
  id = null,
  error = null
} = {}) => {
  return {
    enabled: Boolean(enabled),
    status: String(status || 'skipped').trim() || 'skipped',
    id: Number.isFinite(Number(id)) && Number(id) > 0 ? Number(id) : null,
    error: error ? String(error).trim() : null
  };
};

// Builds the pending request-log payload prior to request execution.
export const createPendingRequestLogPayload = ({
  chatDocId = 0,
  requestId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  chatMode = null,
  intentKey = 'ask',
  requestedModel = null,
  runtimeEnabled = false,
  routingMode = 'hybrid',
  legacyRequest = false,
  attemptsEnabled = false,
  payloadsEnabled = false,
  payloadSampleRate = 0,
  attemptsIndexEnabled = false
} = {}) => {
  return {
    chatDocId: Number(chatDocId || 0),
    requestKey: requestId,
    requestId: null,
    activeProjectId,
    billingOwnerUserId,
    initiatorUserId,
    chatMode,
    intentKey,
    requestedModel,
    status: 'pending',
    metadata: {
      runtimeEnabled: Boolean(runtimeEnabled),
      routingMode: String(routingMode || 'hybrid').trim().toLowerCase() || 'hybrid',
      legacyRequest: Boolean(legacyRequest),
      attemptsEnabled: Boolean(attemptsEnabled),
      payloadsEnabled: Boolean(payloadsEnabled),
      payloadSampleRate: Number.isFinite(Number(payloadSampleRate)) ? Number(payloadSampleRate) : 0,
      attemptsIndexEnabled: Boolean(attemptsIndexEnabled)
    }
  };
};

// Persists pending request-log rows and normalizes the request-log audit state.
export const executePendingRequestLogStage = async ({
  enabled = false,
  currentAudit = null,
  insertChatRequest,
  chatDocId = 0,
  requestId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  chatMode = null,
  intentKey = 'ask',
  requestedModel = null,
  runtimeEnabled = false,
  routingMode = 'hybrid',
  legacyRequest = false,
  attemptsEnabled = false,
  payloadsEnabled = false,
  payloadSampleRate = 0,
  attemptsIndexEnabled = false,
  createPayload = createPendingRequestLogPayload,
  createAuditRecord = createRequestLogAuditRecord,
  onWarn
} = {}) => {
  const fallbackAudit = createAuditRecord({
    enabled,
    status: enabled ? 'pending' : 'skipped',
    id: null,
    error: null
  });
  const priorAudit = currentAudit && typeof currentAudit === 'object'
    ? currentAudit
    : fallbackAudit;

  if (!enabled) {
    return priorAudit;
  }

  try {
    const pendingRequest = typeof insertChatRequest === 'function'
      ? await insertChatRequest(createPayload({
        chatDocId,
        requestId,
        activeProjectId,
        billingOwnerUserId,
        initiatorUserId,
        chatMode,
        intentKey,
        requestedModel,
        runtimeEnabled,
        routingMode,
        legacyRequest,
        attemptsEnabled,
        payloadsEnabled,
        payloadSampleRate,
        attemptsIndexEnabled
      }))
      : null;

    return createAuditRecord({
      enabled,
      status: pendingRequest ? 'pending' : 'skipped',
      id: Number(pendingRequest?.id || 0) || null,
      error: null
    });
  } catch (error) {
    if (typeof onWarn === 'function') {
      onWarn(error?.message || 'chat-request pending log failed');
    }
    return createAuditRecord({
      enabled,
      status: 'failed',
      id: null,
      error: String(error?.message || 'chat-request pending log failed')
    });
  }
};

// Builds the finalized request-log payload using attempt rollups and metadata.
export const createFinalRequestLogPayload = ({
  chatDocId = 0,
  requestId = null,
  providerRequestId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  chatMode = null,
  intentKey = 'ask',
  requestedModel = null,
  responseModel = null,
  rollupUsage = null,
  rollupCostUsd = 0,
  attemptCount = 0,
  failedAttemptCount = 0,
  timeoutAttemptCount = 0,
  totalDurationMs = 0,
  attemptsIndex = [],
  attemptSummary = null,
  attemptsIndexEnabled = false,
  runtimeEnabled = false,
  routingMode = 'hybrid',
  legacyRequest = false,
  finalResponseModel = null,
  finalResponseTokens = 0,
  finalResponseCostUsd = 0,
  finalResponsePromptCostUsd = 0,
  finalResponseCompletionCostUsd = 0,
  status = 'success',
  errorMessage = null
} = {}) => {
  const summaryStats = attemptSummary && typeof attemptSummary === 'object' && attemptSummary.stats && typeof attemptSummary.stats === 'object'
    ? attemptSummary.stats
    : {};
  const rollupPromptCostUsd = Number.isFinite(Number(summaryStats.totalPromptCostUsd))
    ? Number(summaryStats.totalPromptCostUsd)
    : Number.isFinite(Number(finalResponsePromptCostUsd))
      ? Number(finalResponsePromptCostUsd)
      : 0;
  const rollupCompletionCostUsd = Number.isFinite(Number(summaryStats.totalCompletionCostUsd))
    ? Number(summaryStats.totalCompletionCostUsd)
    : Number.isFinite(Number(finalResponseCompletionCostUsd))
      ? Number(finalResponseCompletionCostUsd)
      : 0;

  return {
    chatDocId: Number(chatDocId || 0),
    requestKey: requestId,
    requestId: providerRequestId,
    activeProjectId,
    billingOwnerUserId,
    initiatorUserId,
    chatMode,
    intentKey,
    requestedModel,
    responseModel,
    usage: rollupUsage && typeof rollupUsage === 'object'
      ? rollupUsage
      : { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
    cost: {
      promptCostUsd: rollupPromptCostUsd,
      completionCostUsd: rollupCompletionCostUsd,
      totalCostUsd: Number.isFinite(Number(rollupCostUsd)) ? Number(rollupCostUsd) : 0
    },
    attemptCount: Number(attemptCount || 0) || 0,
    failedAttemptCount: Number(failedAttemptCount || 0) || 0,
    timeoutAttemptCount: Number(timeoutAttemptCount || 0) || 0,
    totalDurationMs: Number(totalDurationMs || 0) || 0,
    attemptsIndex: attemptsIndexEnabled && Array.isArray(attemptsIndex) ? attemptsIndex : [],
    attemptSummary: attemptSummary && typeof attemptSummary === 'object' ? attemptSummary : { stats: {}, attempts: [] },
    status,
    errorMessage,
    metadata: {
      runtimeEnabled: Boolean(runtimeEnabled),
      routingMode: String(routingMode || 'hybrid').trim().toLowerCase() || 'hybrid',
      legacyRequest: Boolean(legacyRequest),
      finalResponseModel: finalResponseModel || responseModel || null,
      finalResponseTokens: Number(finalResponseTokens || 0) || 0,
      finalResponseCostUsd: Number.isFinite(Number(finalResponseCostUsd)) ? Number(finalResponseCostUsd) : 0
    }
  };
};

// Persists the finalized request-log record and returns normalized audit state.
export const executeRequestLogPersistStage = async ({
  enabled = false,
  currentAudit = null,
  insertChatRequest,
  chatDocId = 0,
  requestId = null,
  providerRequestId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  chatMode = null,
  intentKey = 'ask',
  requestedModel = null,
  responseModel = null,
  attemptCount = 0,
  failedAttemptCount = 0,
  timeoutAttemptCount = 0,
  totalDurationMs = 0,
  attemptsIndex = [],
  attemptSummary = null,
  attemptsIndexEnabled = false,
  rollupUsage = null,
  rollupCostUsd = 0,
  runtimeEnabled = false,
  routingMode = 'hybrid',
  legacyRequest = false,
  finalResponseModel = null,
  finalResponseTokens = 0,
  finalResponseCostUsd = 0,
  finalResponsePromptCostUsd = 0,
  finalResponseCompletionCostUsd = 0,
  status = 'success',
  errorMessage = null,
  createAuditRecord = createRequestLogAuditRecord,
  onWarn
} = {}) => {
  const fallbackAudit = (createAuditRecord({
    enabled,
    status: 'skipped',
    id: null,
    error: null
  }) || {});
  const priorAudit = currentAudit && typeof currentAudit === 'object'
    ? currentAudit
    : fallbackAudit;

  if (!enabled) {
    return priorAudit;
  }

  try {
    const logged = typeof insertChatRequest === 'function'
      ? await insertChatRequest(createFinalRequestLogPayload({
        chatDocId,
        requestId,
        providerRequestId,
        activeProjectId,
        billingOwnerUserId,
        initiatorUserId,
        chatMode,
        intentKey,
        requestedModel,
        responseModel,
        attemptCount,
        failedAttemptCount,
        timeoutAttemptCount,
        totalDurationMs,
        attemptsIndex,
        attemptSummary,
        attemptsIndexEnabled,
        rollupUsage,
        rollupCostUsd,
        runtimeEnabled,
        routingMode,
        legacyRequest,
        finalResponseModel,
        finalResponseTokens,
        finalResponseCostUsd,
        finalResponsePromptCostUsd,
        finalResponseCompletionCostUsd,
        status,
        errorMessage
      }))
      : null;

    return createAuditRecord({
      enabled,
      status: logged ? 'success' : priorAudit.status,
      id: Number(logged?.id || priorAudit.id || 0) || null,
      error: null
    });
  } catch (error) {
    if (typeof onWarn === 'function') {
      onWarn(error?.message || 'chat-request logging failed');
    }
    return createAuditRecord({
      enabled,
      status: 'failed',
      id: null,
      error: String(error?.message || 'chat-request logging failed')
    });
  }
};
